import React, { useEffect, useState, startTransition } from 'react';
import {
  AppBar,
  Dialog,
  DialogContent,
  Menu,
  MenuItem,
  Box,
  Container,
  Divider,
  ListItemIcon,
  ListItemText,
  Button,
  Avatar,
  Typography,
  Popover,
  IconButton,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import { useNavigate, useLocation, NavigateFunction } from 'react-router-dom';
import { useAppState } from 'src/state-management/state-provider';
import clientService, { clearAuthTokens } from 'src/clientService';
import {
  Logout as LogoutIcon,
  HomeOutlined,
  HelpOutline,
  FolderOutlined,
  RocketLaunchOutlined,
  ElectricBoltOutlined,
  GroupOutlined,
  VpnKeyOutlined,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  Nature as NatureIcon,
  CloudQueue as CloudQueueIcon,
  AccountBalance as AccountBalanceIcon,
  GraphicEq as GraphicEqIcon,
  Psychology as PsychologyIcon,
  Visibility as VisibilityIcon,
  TravelExplore as TravelExploreIcon,
  Assessment as AssessmentIcon,
  Lightbulb as LightbulbIcon,
  ChevronRight as ChevronRightIcon,
  Close as CloseIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import { profileNavs, navConfig } from './nav.config';
import { Project, UserRole } from 'src/api/Api';
import Logo from 'src/resources/images/logo-white 1.png';

type ProjectSessionStatus = 'upcoming' | 'active' | 'completed';

interface ProjectSession {
  session_id: string;
  project_id: string;
  session_name: string;
  session_start_date: string;
  session_end_date?: string | null;
  status: ProjectSessionStatus;
}

const ACTIVE_SESSION_KEY = 'activeProjectSessionSelection';

const formatSessionDate = (value?: string | null): string => {
  if (!value) return 'Present';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Present';
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatSessionRange = (session?: ProjectSession | null): string => {
  if (!session) return 'Select Session';
  return `${formatSessionDate(session.session_start_date)} - ${session.session_end_date ? formatSessionDate(session.session_end_date) : 'Present'}`;
};

// ── Mega-menu data ────────────────────────────────────────────────────────────
interface ToolItem {
  icon: React.ElementType;
  label: string;
  description: string;
  path: string;
  locked?: boolean;
}

interface ToolColumn {
  heading: string;
  headingIcon: React.ElementType;
  items: ToolItem[];
}

const TOOL_COLUMNS: ToolColumn[] = [
  {
    heading: 'Environmental Intelligence',
    headingIcon: NatureIcon,
    items: [
      { icon: NatureIcon, label: 'Nature Metrics', description: 'Quantify biodiversity, habitat health and ecosystem integrity.', path: '/tool-hub', locked: true },
      { icon: CloudQueueIcon, label: 'Climate Intelligence', description: 'Assess climate risks, vulnerability and adaptation strategies.', path: '/tool-hub', locked: true },
      { icon: AccountBalanceIcon, label: 'TNFD Analytics', description: 'Generate TNFD aligned reports and disclosures.', path: '/tool-hub', locked: true },
    ],
  },
  {
    heading: 'Biodiversity AI',
    headingIcon: GraphicEqIcon,
    items: [
      { icon: GraphicEqIcon, label: 'Bioacoustics', description: 'Detect and classify wildlife sounds and birds species.', path: '/tools/bioacoustic' },
      { icon: PsychologyIcon, label: 'Flora Intelligence', description: 'Identify plant species and assess vegetation health.', path: '/tool-hub', locked: true },
      { icon: VisibilityIcon, label: 'Species Vision', description: 'AI models to identify species using computer vision.', path: '/tool-hub', locked: true },
    ],
  },
  {
    heading: 'Actions & Insights',
    headingIcon: LightbulbIcon,
    items: [
      { icon: TravelExploreIcon, label: 'Analyze Site', description: 'Run advanced analysis on the selected site.', path: '/tool-hub', locked: true },
      { icon: AssessmentIcon, label: 'Generate Report', description: 'Create project or compliance reports instantly.', path: '/tool-hub', locked: true },
      { icon: LightbulbIcon, label: 'Project Insights', description: 'AI-powered insights and recommendations.', path: '/tool-hub', locked: true },
    ],
  },
];

// ── ToolsMegaMenu component ───────────────────────────────────────────────────
interface ToolsMegaMenuProps {
  navigate: NavigateFunction;
  isActive: boolean;
}

const ToolsMegaMenu: React.FC<ToolsMegaMenuProps> = ({ navigate, isActive }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleButtonClick = (event: React.MouseEvent<HTMLElement>) => {
    // Toggle: if already open close it, otherwise open
    if (anchorEl) {
      setAnchorEl(null);
    } else {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleItemClick = (path: string) => {
    setAnchorEl(null);
    startTransition(() => navigate(path));
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <Button
        color="inherit"
        startIcon={<RocketLaunchOutlined sx={{ fontSize: 20 }} />}
        endIcon={
          <KeyboardArrowDownIcon
            sx={{
              fontSize: 16,
              transition: 'transform 0.2s',
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          />
        }
        onClick={handleButtonClick}
        sx={{
          position: 'relative',
          px: 1.5,
          textTransform: 'none',
          fontSize: '14px',
          fontWeight: isActive ? 600 : 500,
          whiteSpace: 'nowrap',
          color: isActive || open ? '#10B981' : '#999',
          '&:after': isActive ? {
            content: '""',
            position: 'absolute',
            bottom: -1,
            left: '0',
            right: '0',
            height: '2px',
            backgroundColor: '#10B981',
          } : {},
          '&:hover': { color: '#FFF', backgroundColor: 'transparent' },
          '& .MuiButton-startIcon': {
            color: isActive || open ? '#10B981' : 'inherit',
          }
        }}
      >
        <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}>Tools</Box>
      </Button>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        disableRestoreFocus
        disableScrollLock
        PaperProps={{
          sx: {
            mt: '8px',
            backgroundColor: '#0F0F0F',
            border: '1px solid #1E1E1E',
            borderRadius: '12px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.6), 0 0 0 1px rgba(16,185,129,0.06)',
            overflow: 'hidden',
            width: '820px',
            p: 0,
          },
        }}
      >
        {/* Dropdown header bar */}
        <Box
          sx={{
            px: 3,
            py: 1.5,
            borderBottom: '1px solid #1A1A1A',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            background: 'linear-gradient(90deg, rgba(16,185,129,0.06) 0%, transparent 60%)',
          }}
        >
          <RocketLaunchOutlined sx={{ fontSize: 14, color: '#10B981' }} />
          <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#10B981', letterSpacing: '1.5px' }}>
            TOOLS
          </Typography>
          <Typography sx={{ fontSize: '11px', color: '#444', ml: 1 }}>
            Powerful environmental intelligence tools
          </Typography>
        </Box>

        {/* 3-column grid */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', p: 0 }}>
          {TOOL_COLUMNS.map((col, colIdx) => {
            const HeadingIcon = col.headingIcon;
            return (
              <Box
                key={col.heading}
                sx={{
                  borderRight: colIdx < TOOL_COLUMNS.length - 1 ? '1px solid #1A1A1A' : 'none',
                  p: '20px 20px 16px',
                }}
              >
                {/* Column heading */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 2 }}>
                  <HeadingIcon sx={{ fontSize: 13, color: '#10B981' }} />
                  <Typography
                    sx={{
                      fontSize: '10px',
                      fontWeight: 700,
                      color: '#10B981',
                      letterSpacing: '1.2px',
                      textTransform: 'uppercase',
                    }}
                  >
                    {col.heading}
                  </Typography>
                </Box>

                {/* Tool items */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  {col.items.map((item) => {
                    const ItemIcon = item.icon;
                    const isLocked = item.locked;
                    return (
                      <Box
                        key={item.label}
                        onClick={() => { if (!isLocked) handleItemClick(item.path); }}
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 1.5,
                          p: '10px 10px',
                          borderRadius: '8px',
                          cursor: isLocked ? 'not-allowed' : 'pointer',
                          opacity: isLocked ? 0.6 : 1,
                          transition: 'all 0.15s ease',
                          '&:hover': !isLocked ? {
                            backgroundColor: 'rgba(16, 185, 129, 0.07)',
                            '& .tool-label': { color: '#FFF' },
                            '& .tool-chevron': { opacity: 1, transform: 'translateX(2px)' },
                            '& .tool-icon-wrap': { borderColor: 'rgba(16,185,129,0.4)', backgroundColor: 'rgba(16,185,129,0.12)' },
                          } : {},
                        }}
                      >
                        {/* Icon */}
                        <Box
                          className="tool-icon-wrap"
                          sx={{
                            width: 34,
                            height: 34,
                            borderRadius: '8px',
                            border: '1px solid #252525',
                            backgroundColor: '#161616',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            transition: 'all 0.15s ease',
                          }}
                        >
                          <ItemIcon sx={{ fontSize: 16, color: '#10B981' }} />
                        </Box>

                        {/* Text */}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            className="tool-label"
                            sx={{
                              fontSize: '13px',
                              fontWeight: 600,
                              color: '#DDD',
                              transition: 'color 0.15s',
                              lineHeight: 1.3,
                              mb: '2px',
                            }}
                          >
                            {item.label}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: '11px',
                              color: '#666',
                              lineHeight: 1.45,
                            }}
                          >
                            {item.description}
                          </Typography>
                        </Box>

                        {/* Chevron or Lock */}
                        {isLocked ? (
                          <LockIcon
                            sx={{
                              fontSize: 14,
                              color: '#555',
                              flexShrink: 0,
                              mt: '9px',
                            }}
                          />
                        ) : (
                          <ChevronRightIcon
                            className="tool-chevron"
                            sx={{
                              fontSize: 14,
                              color: '#555',
                              flexShrink: 0,
                              opacity: 0,
                              transition: 'opacity 0.15s, transform 0.15s',
                              mt: '9px',
                            }}
                          />
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            );
          })}
        </Box>

        {/* Bottom CTA bar */}
        <Box
          sx={{
            px: 3,
            py: 1.5,
            borderTop: '1px solid #1A1A1A',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: 'rgba(10,10,10,0.6)',
          }}
        >
          <Typography sx={{ fontSize: '12px', color: '#555' }}>
            Explore all tools in the Tool Hub
          </Typography>
        </Box>
      </Popover>
    </>
  );
};

// ── ActiveSessionSelector ─────────────────────────────────────────────────
// eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
const ActiveSessionSelector: React.FC<{ navigate: NavigateFunction }> = ({ navigate }) => {
  const { state } = useAppState();
  const [open, setOpen] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [selectedSessionId, setSelectedSessionId] = useState('');
  const [appliedProjectId, setAppliedProjectId] = useState('');
  const [appliedSession, setAppliedSession] = useState<ProjectSession | null>(null);
  const [sessionsByProject, setSessionsByProject] = useState<Record<string, ProjectSession[]>>({});
  const [loadingProjectId, setLoadingProjectId] = useState('');

  const currentProject = state.selectedProject?.projectDetails;
  const projects = currentProject && !state.projects.some((project) => project.project_id === currentProject.project_id)
    ? [currentProject, ...state.projects]
    : state.projects as Project[];
  const selectedProject = projects.find((project) => project.project_id === selectedProjectId);
  const selectedProjectSessions = selectedProjectId ? sessionsByProject[selectedProjectId] || [] : [];
  const displayName = appliedSession ? formatSessionRange(appliedSession) : 'Select Session';

  const fetchSessionsForProject = async (projectId: string) => {
    if (!projectId || sessionsByProject[projectId]) return;
    setLoadingProjectId(projectId);
    try {
      const response = await clientService.request<{ data: ProjectSession[]; count: number }, unknown>({
        path: `/api/v1/projects/${projectId}/sessions`,
        method: 'GET',
        format: 'json',
        secure: true,
      });
      setSessionsByProject((prev) => ({
        ...prev,
        [projectId]: response.data?.data || [],
      }));
    } catch (error) {
      console.error('Failed to load project sessions:', error);
      setSessionsByProject((prev) => ({ ...prev, [projectId]: [] }));
    } finally {
      setLoadingProjectId('');
    }
  };

  useEffect(() => {
    if (!projects || projects.length === 0) return;

    const savedSelection = localStorage.getItem(ACTIVE_SESSION_KEY);
    if (savedSelection) {
      try {
        const parsed = JSON.parse(savedSelection) as {
          projectId?: string;
          session?: ProjectSession;
        };
        if (parsed.projectId && projects.some((project) => project.project_id === parsed.projectId)) {
          setAppliedProjectId(parsed.projectId);
          setSelectedProjectId(parsed.projectId);
          setAppliedSession(parsed.session || null);
          setSelectedSessionId(parsed.session?.session_id || '');
          if (parsed.session) {
            setSessionsByProject((prev) => ({
              ...prev,
              [parsed.projectId as string]: prev[parsed.projectId as string] || [parsed.session as ProjectSession],
            }));
          }
          void fetchSessionsForProject(parsed.projectId);
          return;
        }
      } catch (error) {
        console.error('Failed to parse active session selection:', error);
      }
    }

    const initialProjectId = currentProject?.project_id || projects[0].project_id;
    setAppliedProjectId(initialProjectId);
    setSelectedProjectId(initialProjectId);
    void fetchSessionsForProject(initialProjectId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects, currentProject?.project_id]);

  useEffect(() => {
    if (!selectedProjectId) return;
    void fetchSessionsForProject(selectedProjectId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProjectId]);

  useEffect(() => {
    if (!selectedProjectId || selectedSessionId || loadingProjectId === selectedProjectId) return;
    const sessions = sessionsByProject[selectedProjectId] || [];
    const preferredSession = sessions.find((session) => session.status === 'active') || sessions[0];
    if (preferredSession) {
      setSelectedSessionId(preferredSession.session_id);
      if (!appliedSession && selectedProjectId === appliedProjectId) {
        setAppliedSession(preferredSession);
      }
    }
  }, [appliedProjectId, appliedSession, loadingProjectId, selectedProjectId, selectedSessionId, sessionsByProject]);

  const handleOpen = () => {
    setSelectedProjectId(appliedProjectId || currentProject?.project_id || projects[0]?.project_id || '');
    setSelectedSessionId(appliedSession?.session_id || '');
    setOpen(true);
  };

  const handleProjectSelect = (projectId: string) => {
    setSelectedProjectId(projectId);
    const sessions = sessionsByProject[projectId] || [];
    const preferredSession = sessions.find((session) => session.status === 'active') || sessions[0];
    setSelectedSessionId(preferredSession?.session_id || '');
  };

  const handleApply = () => {
    if (!selectedProjectId) return;
    const nextSession = selectedProjectSessions.find((session) => session.session_id === selectedSessionId) || null;
    setAppliedProjectId(selectedProjectId);
    setAppliedSession(nextSession);
    localStorage.setItem(ACTIVE_SESSION_KEY, JSON.stringify({ projectId: selectedProjectId, session: nextSession }));
    setOpen(false);
    startTransition(() => navigate(`/project/${selectedProjectId}/overview`));
  };

  if (!projects || projects.length === 0) return null;

  return (
    <>
      <Box
        onClick={handleOpen}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          px: 1.5,
          py: 0.75,
          border: '1px solid #222',
          borderRadius: '8px',
          cursor: 'pointer',
          ml: 2,
          '&:hover': { borderColor: '#333' },
          transition: 'border-color 0.2s',
        }}
      >
        <Typography sx={{ fontSize: '10px', color: '#555', letterSpacing: '1px', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
          Active Session:
        </Typography>
        <Typography
          sx={{
            fontSize: '12px',
            fontWeight: 700,
            color: '#10B981',
            maxWidth: '300px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {displayName}
        </Typography>
        <KeyboardArrowDownIcon
          sx={{
            fontSize: 16,
            color: '#10B981',
            transition: 'transform 0.2s',
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
        />
      </Box>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            background: '#0F0F0F',
            border: '1px solid #222',
            borderRadius: '14px',
            color: '#fff',
            overflow: 'hidden',
          },
        }}
      >
        <Box
          sx={{
            px: 3,
            py: 2,
            borderBottom: '1px solid #1F1F1F',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box>
            <Typography sx={{ fontSize: '18px', fontWeight: 700 }}>Select Active Session</Typography>
            <Typography sx={{ mt: 0.5, fontSize: '12px', color: '#777' }}>
              Choose a project and monitoring session for this workspace.
            </Typography>
          </Box>
          <IconButton onClick={() => setOpen(false)} sx={{ color: '#777', '&:hover': { color: '#fff' } }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(260px, 0.9fr) 1.1fr', minHeight: 360 }}>
            <Box sx={{ borderRight: '1px solid #1F1F1F', p: 2 }}>
              <Typography sx={{ px: 1, mb: 1.5, fontSize: '10px', color: '#666', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase' }}>
                Projects
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, maxHeight: 310, overflowY: 'auto', pr: 0.5 }}>
                {projects.map((project) => {
                  const isSelected = project.project_id === selectedProjectId;
                  return (
                    <Box
                      key={project.project_id}
                      onClick={() => handleProjectSelect(project.project_id)}
                      sx={{
                        p: 1.25,
                        borderRadius: '8px',
                        cursor: 'pointer',
                        border: `1px solid ${isSelected ? 'rgba(16,185,129,0.45)' : 'transparent'}`,
                        background: isSelected ? 'rgba(16,185,129,0.1)' : 'transparent',
                        '&:hover': { background: isSelected ? 'rgba(16,185,129,0.12)' : 'rgba(255,255,255,0.04)' },
                      }}
                    >
                      <Typography sx={{ fontSize: '13px', fontWeight: 700, color: isSelected ? '#10B981' : '#fff' }}>
                        {project.project_name}
                      </Typography>
                      <Typography sx={{ mt: 0.25, fontSize: '11px', color: '#666' }}>
                        {project.project_type || project.project_status || 'Project'}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>

            <Box sx={{ p: 3 }}>
              <Typography sx={{ fontSize: '10px', color: '#666', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase' }}>
                Sessions
              </Typography>
              <Typography sx={{ mt: 0.75, mb: 3, fontSize: '18px', fontWeight: 700 }}>
                {selectedProject?.project_name || 'Select a project'}
              </Typography>

              <FormControl fullWidth>
                <InputLabel sx={{ color: '#777', '&.Mui-focused': { color: '#10B981' } }}>
                  Project Session
                </InputLabel>
                <Select
                  label="Project Session"
                  value={selectedSessionId}
                  onChange={(event) => setSelectedSessionId(event.target.value as string)}
                  disabled={!selectedProjectId || loadingProjectId === selectedProjectId || selectedProjectSessions.length === 0}
                  sx={{
                    backgroundColor: '#151515',
                    color: '#fff',
                    borderRadius: '8px',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#333' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(16,185,129,0.45)' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#10B981' },
                    '& .MuiSvgIcon-root': { color: '#777' },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        backgroundColor: '#151515',
                        color: '#fff',
                        border: '1px solid #333',
                      },
                    },
                  }}
                >
                  {selectedProjectSessions.map((session) => (
                    <MenuItem key={session.session_id} value={session.session_id} sx={{ '&:hover': { backgroundColor: '#222' } }}>
                      {formatSessionRange(session)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  borderRadius: '8px',
                  background: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(255,255,255,0.06)',
                }}
              >
                <Typography sx={{ fontSize: '11px', color: '#666', fontWeight: 700, textTransform: 'uppercase', mb: 0.75 }}>
                  Selected Range
                </Typography>
                <Typography sx={{ fontSize: '13px', color: selectedSessionId ? '#fff' : '#777', fontWeight: 600 }}>
                  {loadingProjectId === selectedProjectId
                    ? 'Loading sessions...'
                    : formatSessionRange(selectedProjectSessions.find((session) => session.session_id === selectedSessionId))}
                </Typography>
                {!loadingProjectId && selectedProjectId && selectedProjectSessions.length === 0 && (
                  <Typography sx={{ mt: 1, fontSize: '12px', color: '#777' }}>
                    No sessions found for this project.
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        </DialogContent>

        <Box
          sx={{
            px: 3,
            py: 2,
            borderTop: '1px solid #1F1F1F',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 1.5,
          }}
        >
          <Button onClick={() => setOpen(false)} sx={{ color: '#999', fontWeight: 700 }}>
            Close
          </Button>
          <Button
            variant="contained"
            onClick={handleApply}
            disabled={!selectedProjectId || !selectedSessionId}
            sx={{ bgcolor: '#10B981', color: '#07110d', fontWeight: 800, '&:hover': { bgcolor: '#059669' } }}
          >
            Apply
          </Button>
        </Box>
      </Dialog>
    </>
  );
};

// ── Header ────────────────────────────────────────────────────────────────────
const Header: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [userMenuAnchorEl, setUserMenuAnchorEl] = useState<null | HTMLElement>(null);
  const { state, dispatch } = useAppState();

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchorEl(null);
  };

  const handleLogoClick = () => {
    const projectId = state.selectedProject?.projectDetails?.project_id;
    const isAdmin = state.user?.role.includes(UserRole.Admin);

    if (projectId) {
      startTransition(() => {
        if (isAdmin) {
          navigate(`/admin/project/${projectId}`);
        } else {
          navigate(`/project/${projectId}/overview`);
        }
      });
    } else {
      startTransition(() => {
        navigate('/landing');
      });
    }
  };

  const handleLogout = async () => {
    handleUserMenuClose();
    try {
      await clientService.request({
        path: '/api/v1/auth/logout',
        method: 'POST',
        secure: true,
      });
    } catch (error) {
      console.error('Logout API call failed:', error);
    }
    clearAuthTokens();
    startTransition(() => {
      dispatch({ type: 'LOGOUT' });
      navigate('/auth');
    });
  };

  const isAdmin = state.user?.role.includes(UserRole.Admin);
  const isUserOrEmployee = state.user?.role.includes(UserRole.User) || state.user?.role.includes(UserRole.Employee);

  const getAdminIcon = (text?: string) => {
    if (!text) return null;
    switch (text) {
      case 'Projects': return <FolderOutlined sx={{ fontSize: 20 }} />;
      case 'Deployments': return <RocketLaunchOutlined sx={{ fontSize: 20 }} />;
      case 'Devices': return <ElectricBoltOutlined sx={{ fontSize: 20 }} />;
      case 'Organisations': return <GroupOutlined sx={{ fontSize: 20 }} />;
      case 'API Management': return <VpnKeyOutlined sx={{ fontSize: 20 }} />;
      default: return null;
    }
  };

  return (
    <AppBar
      position="fixed"
      className="header"
      sx={{
        backgroundColor: '#0A0A0A',
        color: '#FFF',
        boxShadow: 'none',
        borderBottom: '1px solid #1A1A1A',
        zIndex: (theme) => theme.zIndex.drawer + 1
      }}
    >
      <Container
        maxWidth={false}
        sx={{
          maxWidth: '1736px',
          display: 'flex',
          justifyContent: 'space-between',
          px: { xs: 1, sm: 3 },
          overflowX: 'auto',
          '&::-webkit-scrollbar': { display: 'none' },
          scrollbarWidth: 'none',
        }}
      >
        {/* Left: Logo + Active Session */}
        <Box display="flex" alignItems="center" sx={{ minHeight: "72px" }}>
          <Box className="logo" display="flex" alignItems="center" height="100%" onClick={handleLogoClick} sx={{ cursor: 'pointer' }}>
            <img src={Logo} alt="Logo" style={{ height: '24px' }} />
          </Box>
          {/* {isUserOrEmployee && location.pathname !== '/landing' && <ActiveSessionSelector navigate={navigate} />} */}
        </Box>

        <Box display="flex" alignItems="center" sx={{ minHeight: "73px", flexShrink: 0 }}>
          <Box sx={{ display: 'flex', gap: { xs: 0, sm: 0.5 }, ml: { xs: 0, sm: 1 } }}>
            {/* Home - Common */}
            <Button
              color="inherit"
              startIcon={<HomeOutlined sx={{ fontSize: 20 }} />}
              onClick={() => navigate('/landing')}
              sx={{
                position: 'relative',
                px: 1,
                textTransform: 'none',
                fontSize: '14px',
                fontWeight: location.pathname === '/landing' ? 600 : 500,
                whiteSpace: 'nowrap',
                color: location.pathname === '/landing' ? '#10B981' : '#999',
                '&:after': location.pathname === '/landing' ? {
                  content: '""',
                  position: 'absolute',
                  bottom: -1,
                  left: '0',
                  right: '0',
                  height: '2px',
                  backgroundColor: '#10B981',
                } : {},
                '&:hover': { color: '#FFF', backgroundColor: 'transparent' },
                '& .MuiButton-startIcon': {
                  color: location.pathname === '/landing' ? '#10B981' : 'inherit'
                }
              }}
            >
              <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}>Home</Box>
            </Button>

            {/* Role Specific Items */}
            {isAdmin && navConfig.ADMIN.map((item) => {
              const path = item.path || '';
              const isActive = location.pathname.startsWith(path);
              return (
                <Button
                  key={item.text || item.path}
                  color="inherit"
                  startIcon={getAdminIcon(item.text)}
                  onClick={() => navigate(path)}
                  sx={{
                    position: 'relative',
                    px: 1.5,
                    textTransform: 'none',
                    fontSize: '14px',
                    fontWeight: isActive ? 600 : 500,
                    color: isActive ? '#10B981' : '#999',
                    whiteSpace: 'nowrap',
                    minWidth: 'fit-content',
                    '&:after': isActive ? {
                      content: '""',
                      position: 'absolute',
                      bottom: -1,
                      left: '0',
                      right: '0',
                      height: '2px',
                      backgroundColor: '#10B981',
                    } : {},
                    '&:hover': {
                      color: isActive ? '#10B981' : '#FFF',
                      backgroundColor: 'transparent',
                    },
                    '& .MuiButton-startIcon': {
                      color: isActive ? '#10B981' : 'inherit'
                    }
                  }}
                >
                  <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}>{item.text}</Box>
                </Button>
              );
            })}

            {/* User/Employee: Tools mega-menu */}
            {!isAdmin && isUserOrEmployee && (
              <ToolsMegaMenu navigate={navigate} isActive={location.pathname === '/tool-hub'} />
            )}

            {/* Help & Support - Common */}
            <Button
              color="inherit"
              startIcon={<HelpOutline sx={{ fontSize: 20 }} />}
              onClick={() => navigate('/help-support')}
              sx={{
                position: 'relative',
                px: 1.5,
                textTransform: 'none',
                fontSize: '14px',
                fontWeight: location.pathname === '/help-support' ? 600 : 500,
                whiteSpace: 'nowrap',
                color: location.pathname === '/help-support' ? '#10B981' : '#999',
                '&:after': location.pathname === '/help-support' ? {
                  content: '""',
                  position: 'absolute',
                  bottom: -1,
                  left: '0',
                  right: '0',
                  height: '2px',
                  backgroundColor: '#10B981',
                } : {},
                '&:hover': { color: '#FFF', backgroundColor: 'transparent' },
                '& .MuiButton-startIcon': {
                  color: location.pathname === '/help-support' ? '#10B981' : 'inherit'
                }
              }}
            >
              <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}>Help & Support</Box>
            </Button>
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          <Box
            onClick={handleUserMenuOpen}
            sx={{
              ml: { xs: 0.5, sm: 2 },
              pl: { xs: 1, sm: 2 },
              borderLeft: '1px solid #222',
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 0.5, sm: 1.5 },
              cursor: 'pointer',
              height: '40px',
              flexShrink: 0,
            }}
          >
            <Avatar
              sx={{
                width: 32,
                height: 32,
                borderRadius: '8px',
                fontSize: '14px',
                backgroundColor: '#10B981'
              }}
            >
              {(state.user?.first_name || 'D').charAt(0).toUpperCase()}
            </Avatar>
            <Typography sx={{ color: '#FFF', fontSize: '14px', fontWeight: 500 }}>
              {state.user?.first_name || 'Darukaa'}
            </Typography>
            <KeyboardArrowDownIcon sx={{ color: '#666', fontSize: 18 }} />
          </Box>
          <Menu
            anchorEl={userMenuAnchorEl}
            open={Boolean(userMenuAnchorEl)}
            onClose={handleUserMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            PaperProps={{
              sx: {
                backgroundColor: '#111',
                color: '#FFF',
                border: '1px solid #222',
                mt: 1,
                minWidth: '200px',
                '& .MuiMenuItem-root': {
                  fontSize: '14px',
                  py: 1.5,
                  '&:hover': {
                    backgroundColor: 'rgba(16, 185, 129, 0.1)',
                    color: '#10B981',
                  },
                },
              },
            }}
          >
            <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #222' }}>
              <Typography sx={{ fontSize: '14px', fontWeight: 700 }}>Hi, {state.user?.first_name || 'Darukaa'}</Typography>
            </Box>
            {profileNavs.map((item) => (
              <MenuItem
                key={item.text}
                onClick={() => {
                  handleUserMenuClose();
                  startTransition(() => {
                    navigate(item.path);
                  });
                }}
              >
                <ListItemIcon sx={{ color: 'inherit', minWidth: '35px' }}>
                  <item.icon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={item.text} />
              </MenuItem>
            ))}
            <Divider sx={{ backgroundColor: '#222' }} />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon sx={{ color: 'inherit', minWidth: '35px' }}>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Logout" />
            </MenuItem>
          </Menu>
        </Box>
      </Container>
    </AppBar>
  );
};

export default Header;
